import { useContext } from "react";
import { DataControlerContext } from "context/DataControlerContext";
import { getTokenByActionAndResourceId, imageBitmapToBlob } from "lib/utils";
import {
  PresetConfigParamsReq,
  PresetWebrtcAction,
  PresetConfigListRes,
  CameraType,
} from "webrtc-player/common/base";
import { AuthContext } from "context/AuthContext";
import { ResDeviceType } from "webrtc-player/common/base";
import { PresetSnapshotType } from "webrtc-player/webrtc/data_webrtc";
import { SignalContext } from "context/SignalContext";
import { DB_STORE } from "lib/constants";

export type PresetType = {
  id?: number;
  name?: string;
  //empty: boolean;
  modifiedAt?: number;
};

export default function usePresetRtcService() {
  const { getControllerManagerWithWaiter } = useContext(DataControlerContext);
  // const [getDailyRecord,setGetDailyRecord] = useState<(nvrId:number,camera:Camera,streamId:number,year:number,month:number)=>Promise<RecordDailyRsp>>(null)
  const { user } = useContext(AuthContext);
  const { getDb } = useContext(SignalContext);
  const actions = [
    PresetWebrtcAction.LIST,
    PresetWebrtcAction.DELETE,
    PresetWebrtcAction.GET_IMG,
    PresetWebrtcAction.GOTO,
    PresetWebrtcAction.SET,
  ];
  async function getPresetList(nvrId: number, camera: CameraType) {
    const dataControllerManager = await getControllerManagerWithWaiter();
    return new Promise(
      (resolve: (res: PresetConfigListRes) => void, reject) => {
        getTokenByActionAndResourceId(
          actions,
          camera.id,
          user,
          ResDeviceType.CAMERA,
        ).then((res) => {
          const [{ token }] = res;
          dataControllerManager
            .asyncGetDataController(+nvrId)
            .then((controller) => {
              const req: PresetConfigParamsReq = {
                camera,
                token,
              };
              controller.presetList(req, (error, result) => {
                if (error) {
                  reject(error);
                }
                resolve(result);
              });
            });
        });
      },
    );
  }
  async function setPreset(
    nvrId: number,
    camera: CameraType,
    id: number,
    name: string,
  ) {
    const dataControllerManager = await getControllerManagerWithWaiter();

    return new Promise((resolve, reject) => {
      getTokenByActionAndResourceId(
        actions,
        camera.id,
        user,
        ResDeviceType.CAMERA,
      ).then((res) => {
        const [{ token }] = res;
        dataControllerManager
          .asyncGetDataController(+nvrId)
          .then((controller) => {
            const req: PresetConfigParamsReq = {
              camera,
              token,
              id,
              name,
            };
            controller.presetSet(req, (error, result) => {
              if (error) {
                reject(error);
              }
              resolve(result);
            });
          });
      });
    });
  }
  async function gotoPreset(nvrId: number, camera: CameraType, id: number) {
    const dataControllerManager = await getControllerManagerWithWaiter();
    return new Promise((resolve, reject) => {
      getTokenByActionAndResourceId(
        actions,
        camera.id,
        user,
        ResDeviceType.CAMERA,
      ).then((res) => {
        const [{ token }] = res;
        dataControllerManager
          ?.asyncGetDataController(+nvrId)
          .then((controller) => {
            const req: PresetConfigParamsReq = {
              camera,
              token,
              id,
            };
            controller.presetGoto(req, (error, result) => {
              if (error) {
                reject(error);
              }
              resolve(result);
            });
          });
      });
    });
  }
  async function deletePreset(nvrId: number, camera: CameraType, id: number) {
    const dataControllerManager = await getControllerManagerWithWaiter();
    return new Promise((resolve, reject) => {
      getTokenByActionAndResourceId(
        actions,
        camera.id,
        user,
        ResDeviceType.CAMERA,
      ).then((res) => {
        const [{ token }] = res;
        dataControllerManager
          ?.asyncGetDataController(+nvrId)
          .then((controller) => {
            const req: PresetConfigParamsReq = {
              camera,
              token,
              id,
            };
            controller.presetDelete(req, (error) => {
              if (error) {
                reject(error);
              }
              resolve(void 0);
            });
          });
      });
    });
  }
  async function getPresetSnapShotWithDbCache(
    nvrId: number,
    camera: CameraType,
    presetId: number,
    presetModifiedAt: string | number,
    timeout = 15000,
  ): Promise<Blob> {
    const db = await getDb();
    const storedPreset = await db.read(
      DB_STORE.PRESET,
      DB_STORE.PRESET + "_" + camera.id + "_" + presetId,
    );
    if (presetModifiedAt.toString() != storedPreset?.modifiedAt) {
      const snapShot = await getPresetSnapshot(
        nvrId,
        camera,
        presetId,
        timeout,
      );
      const blob = await imageBitmapToBlob(snapShot?.snapshot.imageBitmap);
      db.put(DB_STORE.PRESET, {
        id: DB_STORE.PRESET + "_" + camera.id + "_" + presetId,
        imageBlob: blob,
        modifiedAt: presetModifiedAt,
      });
      return blob;
    } else {
      return storedPreset.imageBlob;
    }
  }
  async function getPresetSnapshot(
    nvrId: number,
    camera: CameraType,
    presetId: number,
    timeout = 15000,
  ) {
    const dataControllerManager = await getControllerManagerWithWaiter();
    return new Promise((resolve: (res: PresetSnapshotType) => void, reject) => {
      getTokenByActionAndResourceId(
        actions,
        camera.id,
        user,
        ResDeviceType.CAMERA,
      ).then((res) => {
        const [{ token }] = res;
        dataControllerManager
          ?.asyncGetDataController(+nvrId)
          .then((controller) => {
            const req: PresetConfigParamsReq = {
              camera,
              token,
              id: presetId,
              speed: 10,
            };
            controller.presetGetImg(req, (error, result) => {
              if (error || !result) {
                reject(error);
              }
              const snapshot = result?.data as PresetSnapshotType;
              resolve(snapshot);
            });
          });
      });
    });
  }

  return {
    getPresetSnapshot,
    gotoPreset,
    getPresetList,
    setPreset,
    deletePreset,
    getPresetSnapShotWithDbCache,
  };
}
